/**
 * Global search palette.
 *
 * Triggered by Ctrl+/ (⌘+/ on Mac). A calm overlay panel — visually a sibling
 * of Quick Capture — with a single text input that searches task title and
 * description as you type. Behaviour:
 *
 *   - debounced (~200 ms) fetch of `/tasks?q=…` via `useTasksSearch`
 *   - min 2 chars; below that we show a hint instead of results
 *   - scope chips (Open · All · Done · Archived) map to the `scope` param
 *   - results reuse the shared `toRowModel` adapter + `TaskRow` primitive
 *   - ↑/↓ move the selection, Enter opens it, Esc closes
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { TaskRow } from '@k-os/ui';
import { useTasksSearch, type TasksScope } from '../api/tasks';
import { toRowModel } from '../screens/_task-row';
import { useOpenTask } from '../screens/_use-open-task';
import styles from './SearchPalette.module.css';

const MIN_CHARS = 2;
const DEBOUNCE_MS = 200;

interface ScopeOption {
  id: TasksScope;
  label: string;
}

const SCOPE_OPTIONS: ScopeOption[] = [
  { id: 'open', label: 'Open' },
  { id: 'all', label: 'All' },
  { id: 'done', label: 'Done' },
  { id: 'archived', label: 'Archived' },
];

export interface SearchPaletteProps {
  open: boolean;
  onClose: () => void;
}

export function SearchPalette({ open, onClose }: SearchPaletteProps) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [scope, setScope] = useState<TasksScope>('open');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const openTask = useOpenTask();

  // Reset everything whenever the palette opens, then focus the input.
  useEffect(() => {
    if (open) {
      setQuery('');
      setDebounced('');
      setScope('open');
      setActiveIdx(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Debounce the query → debounced term used for the fetch. The timer is torn
  // down on every keystroke and on unmount so no stale fetch fires.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]);

  const trimmed = debounced.trim();
  const belowMin = trimmed.length < MIN_CHARS;
  const search = useTasksSearch(debounced, scope);

  const rows = useMemo(
    () =>
      (search.data?.tasks ?? []).map((t) => ({
        task: t,
        model: toRowModel(t, { showUpdatedSince: true }),
      })),
    [search.data],
  );

  // Reset the selection to the top when the search term or scope changes.
  useEffect(() => {
    setActiveIdx(0);
  }, [trimmed, scope]);

  // Clamp the selection when the result count shrinks — e.g. a background
  // refetch that drops rows out from under an in-range activeIdx would
  // otherwise leave it pointing past the end, making Enter a no-op.
  useEffect(() => {
    setActiveIdx((i) => Math.min(i, Math.max(0, rows.length - 1)));
  }, [rows.length]);

  if (!open) return null;

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      onClose();
      return;
    }
    if (rows.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => (i + 1) % rows.length);
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => (i - 1 + rows.length) % rows.length);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      const pick = rows[activeIdx];
      if (pick) {
        openTask(pick.model);
        onClose();
      }
    }
  };

  const showResults = !belowMin && !search.isLoading && !search.isError;

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={styles.modal}
        onMouseDown={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Search tasks"
      >
        <div className={styles.head}>
          <span className={styles.headLabel}>Search</span>
          <span className={styles.headHint}>
            Search task titles and notes · <span className={styles.kbd}>esc</span> to close
          </span>
        </div>

        <input
          ref={inputRef}
          className={styles.input}
          placeholder="Search tasks…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          autoFocus
        />

        <div className={styles.scopeRow}>
          {SCOPE_OPTIONS.map((opt) => (
            <button
              key={opt.id}
              type="button"
              className={`${styles.scopeChip} ${
                scope === opt.id ? styles.scopeChipActive : ''
              }`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setScope(opt.id);
                inputRef.current?.focus();
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>

        <div className={styles.results}>
          {belowMin && (
            <div className={styles.hint}>Type at least {MIN_CHARS} characters to search.</div>
          )}
          {!belowMin && search.isLoading && <div className={styles.hint}>Searching…</div>}
          {!belowMin && search.isError && (
            <div className={styles.error}>Couldn’t search. Please try again.</div>
          )}
          {showResults && rows.length === 0 && (
            <div className={styles.empty}>No tasks match “{trimmed}”.</div>
          )}
          {showResults && rows.length > 0 && (
            <div className={styles.list} role="listbox">
              {rows.map((r, i) => (
                <div
                  key={r.task.id}
                  role="option"
                  aria-selected={i === activeIdx}
                  className={`${styles.rowWrap} ${i === activeIdx ? styles.rowWrapActive : ''}`}
                  onMouseEnter={() => setActiveIdx(i)}
                >
                  <TaskRow
                    task={r.model}
                    showStatus
                    onOpen={(t) => {
                      openTask(t);
                      onClose();
                    }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <span>
            <span className={styles.kbd}>↑↓</span> navigate
          </span>
          <span>
            <span className={styles.kbd}>↵</span> open
          </span>
          <span>
            <span className={styles.kbd}>esc</span> close
          </span>
        </div>
      </div>
    </div>
  );
}
